import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import toast from 'react-hot-toast';
import {
  Palette,
  Download,
  Sparkles,
  Image,
  Settings,
  BookOpen,
  ArrowRight,
  RefreshCw,
  Check,
  X,
  Info,
  Clock,
  Eye,
  Loader,
  Coins,
  Trash2
} from 'lucide-react';

const CoverGeneratorPage = () => {
  const { user, API_BASE_URL, credits, refreshCredits, checkCreditsForOperation } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingCovers, setLoadingCovers] = useState(true);
  const [covers, setCovers] = useState([]);
  const [selectedCover, setSelectedCover] = useState(null);
  const [kdpSpecs, setKdpSpecs] = useState(null);
  const [creditCheck, setCreditCheck] = useState(null);
  const [deletingCover, setDeletingCover] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    author_name: user?.full_name || '',
    genre: 'Select Genre',
    provider: 'fal',
    style_preferences: '',
    mood: '',
    target_audience: '',
    variations: 3,
    format_size: '6x9',
    page_count: 100,
    kdp_mode: true // KDP mode on by default
  });

  const genres = [
    'Select Genre',
    'General', 'Fiction', 'Non-Fiction', 'Business', 'Self-Help',
    'Romance', 'Mystery', 'Thriller', 'Science Fiction', 'Fantasy',
    'Horror', 'Biography', 'History', 'Travel', 'Cooking',
    'Technology', 'Health', 'Education', 'Children', 'Poetry'
  ];

  const providers = [
    { id: 'fal', name: 'FAL.ai', description: 'High quality, fast generation' },
    { id: 'openai', name: 'OpenAI DALL-E', description: 'Creative and detailed' },
    { id: 'gemini', name: 'Google Gemini', description: 'Versatile and reliable' }
  ];

  const kdpFormats = [
    { value: '6x9', label: '6" × 9" (Standard Fiction)', popular: true },
    { value: '5x8', label: '5" × 8" (Mass Market)' },
    { value: '8.5x11', label: '8.5" × 11" (Textbook/Manual)' },
    { value: '7x10', label: '7" × 10" (Non-fiction)' },
    { value: '8x10', label: '8" × 10" (Photo Book)' }
  ];

  const ebookSizes = [
    { value: '1600x2560', label: '1600 × 2560 (Standard eBook)', popular: true },
    { value: '1200x1920', label: '1200 × 1920 (Compact eBook)' },
    { value: '1800x2700', label: '1800 × 2700 (High Resolution)' }
  ];

  useEffect(() => {
    loadUserCovers();
    loadCreditCheck();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const loadUserCovers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/covers/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCovers(data.covers || []);
      } else {
        console.error('Failed to load covers');
      }
    } catch (error) {
      console.error('Error loading covers:', error);
    } finally {
      setLoadingCovers(false);
    }
  };

  const loadCreditCheck = async () => {
    // First refresh credits to get latest balance
    if (refreshCredits) {
      await refreshCredits();
    }
    
    if (checkCreditsForOperation) {
      const result = await checkCreditsForOperation('cover_generation');
      setCreditCheck(result);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleKdpModeToggle = (e) => {
    const isKdpMode = e.target.checked;
    setFormData(prev => ({
      ...prev,
      kdp_mode: isKdpMode,
      format_size: isKdpMode ? '6x9' : '1600x2560' // Reset to default for each mode
    }));
  };

  const getCoverBadge = (cover) => {
    const coverType = cover.cover_type || 'unknown';
    
    const badgeConfig = {
      kdp_cover: {
        text: 'KDP Cover',
        className: 'bg-purple-500/80 text-white'
      },
      ebook_cover: {
        text: 'eBook Cover', 
        className: 'bg-blue-500/80 text-white'
      },
      book_cover: {
        text: 'Book Cover',
        className: 'bg-green-500/80 text-white'
      },
      unknown: {
        text: cover.source === 'book' ? 'Book Cover' : 'Generated',
        className: cover.source === 'book' ? 'bg-green-500/80 text-white' : 'bg-gray-500/80 text-white'
      }
    };

    const badge = badgeConfig[coverType] || badgeConfig.unknown;
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full font-medium ${badge.className}`}>
        {badge.text}
      </span>
    );
  };

  const CoverDetailsModal = ({ cover, onClose }) => {
    if (!cover) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">{cover.title}</h2>
              <p className="text-gray-400 mt-1">Cover Details</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Cover Image */}
              <div className="space-y-4">
                <div className="aspect-[3/4] bg-gray-800 rounded-lg overflow-hidden border border-gray-600 relative flex items-center justify-center">
                  <img
                    src={`data:image/png;base64,${cover.image_base64}`}
                    alt={`Cover for ${cover.title}`}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      console.error('Cover image failed to load');
                      e.target.style.display = 'none';
                    }}
                  />
                  
                  {/* Badge */}
                  <div className="absolute top-3 left-3">
                    {getCoverBadge(cover)}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => downloadCover(cover)}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download Cover</span>
                  </button>
                </div>
              </div>
              
              {/* Cover Details */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Cover Information</h3>
                  <div className="space-y-3">
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">Title:</span>
                          <p className="text-white font-medium">{cover.title}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Author:</span>
                          <p className="text-white font-medium">{cover.author}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Genre:</span>
                          <p className="text-white font-medium capitalize">{cover.genre}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Created:</span>
                          <p className="text-white font-medium">{new Date(cover.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Technical Details */}
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h4 className="text-white font-medium mb-3">Technical Specifications</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">Type:</span>
                          <p className="text-white font-medium">
                            {cover.cover_type === 'kdp_cover' ? 'KDP Cover' :
                             cover.cover_type === 'ebook_cover' ? 'eBook Cover' :
                             cover.cover_type === 'book_cover' ? 'Book Cover' : 'Generated Cover'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-400">Format:</span>
                          <p className="text-white font-medium">
                            {cover.book_format === 'paperback' ? 'Paperback (KDP)' : 
                             cover.book_format === 'ebook' ? 'eBook' : 'Unknown'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-400">Dimensions:</span>
                          <p className="text-white font-medium">
                            {cover.dimensions || `${cover.width}×${cover.height}px`}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-400">Provider:</span>
                          <p className="text-white font-medium capitalize">{cover.provider || 'Unknown'}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* AI Prompt */}
                    {cover.prompt && (
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <h4 className="text-white font-medium mb-3">AI Generation Prompt</h4>
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {cover.prompt}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const DeleteConfirmModal = ({ cover, onClose, onConfirm }) => {
    if (!cover) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-2xl max-w-md w-full">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-red-500/20 p-3 rounded-full">
                <Trash2 className="h-6 w-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Delete Cover</h3>
                <p className="text-gray-400 text-sm">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-300">
                Are you sure you want to delete the cover for "{cover.title}"?
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-700 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => onConfirm(cover)}
                disabled={deletingCover === cover.id}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {deletingCover === cover.id ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Cover'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const generateCovers = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a book title');
      return;
    }

    if (formData.genre === 'Select Genre') {
      toast.error('Please select a genre');
      return;
    }

    if (!creditCheck?.sufficient) {
      toast.error(`Insufficient credits. Need ${creditCheck?.required}, have ${creditCheck?.current_balance}`);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/covers/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: formData.title,
          author_name: formData.author_name,
          genre: formData.genre,
          provider: formData.provider,
          style_preferences: formData.style_preferences,
          mood: formData.mood,
          target_audience: formData.target_audience,
          number_of_variations: formData.variations,
          book_format: formData.kdp_mode ? "paperback" : "ebook",
          dimensions: formData.format_size,
          page_count: formData.kdp_mode ? formData.page_count : 100
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Generated ${data.covers.length} cover variations successfully!`);
        
        // Refresh covers and credits
        await loadUserCovers();
        if (refreshCredits) {
          await refreshCredits();
        }
        
        // Update credit check
        await loadCreditCheck();
        
      } else {
        const error = await response.json();
        if (response.status === 402) {
          toast.error('Insufficient credits. Please purchase more credits.');
        } else {
          toast.error(error.detail || 'Failed to generate covers');
        }
      }
    } catch (error) {
      console.error('Error generating covers:', error);
      toast.error('Failed to generate covers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadCover = (cover) => {
    try {
      const imageData = `data:image/png;base64,${cover.image_base64}`;
      const link = document.createElement('a');
      link.href = imageData;
      link.download = `${cover.title.replace(/[^a-zA-Z0-9]/g, '_')}_cover.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Cover downloaded successfully!');
    } catch (error) {
      console.error('Error downloading cover:', error);
      toast.error('Failed to download cover');
    }
  };

  const deleteCover = async (cover) => {
    setDeletingCover(cover.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/covers/${cover.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Cover deleted successfully!');
        setShowDeleteConfirm(null);
        // Refresh the covers list
        loadUserCovers();
      } else {
        throw new Error('Failed to delete cover');
      }
    } catch (error) {
      console.error('Error deleting cover:', error);
      toast.error('Failed to delete cover');
    } finally {
      setDeletingCover(null);
    }
  };

  const confirmDelete = (cover) => {
    setShowDeleteConfirm(cover);
  };

  return (
    <>
      <SEOHead 
        title="AI Book Cover Generator - Professional KDP & eBook Covers"
        description="Create stunning book covers with AI in seconds. Professional designs for Amazon KDP, eBooks, and print books. Choose from hundreds of templates and customization options."
        keywords="book cover generator, AI book covers, KDP cover design, eBook cover maker, book cover design tool, professional book covers, Amazon book covers"
        canonical="/kdp-covers"
        ogTitle="AI Book Cover Generator - Create Professional Covers in Seconds"
        ogDescription="Generate beautiful book covers with AI. Perfect for Amazon KDP, eBooks, and print books. Professional templates and instant downloads."
        ogImage="/book-cover-examples.png"
        ogUrl="/kdp-covers"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "AI Book Cover Generator",
          "description": "Create professional book covers with AI for Amazon KDP and eBook publishing",
          "applicationCategory": "DesignApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free trial with credits"
          }
        }}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {formData.kdp_mode ? 'KDP Cover Generator' : 'Book Cover Generator'}
          </h1>
          <p className="text-lg text-gray-300">
            {formData.kdp_mode 
              ? 'Create professional book covers with exact Amazon KDP specifications'
              : 'Create beautiful book covers for eBooks and general publishing'
            }
          </p>
        </div>

        {/* Credit Cost Display */}
        {creditCheck && (
          <div className={`mb-8 p-4 rounded-lg border ${
            creditCheck.sufficient 
              ? 'bg-blue-500/20 border-blue-500/30' 
              : 'bg-red-500/20 border-red-500/30'
          }`}>
            <div className="flex items-center justify-center space-x-2">
              <Coins className={`h-5 w-5 ${
                creditCheck.sufficient ? 'text-blue-400' : 'text-red-400'
              }`} />
              <span className={`font-medium ${
                creditCheck.sufficient ? 'text-blue-300' : 'text-red-300'
              }`}>
                {creditCheck.sufficient 
                  ? `Cover generation costs ${creditCheck.required} credits. You have ${creditCheck.current_balance} credits.`
                  : `Insufficient credits! Need ${creditCheck.required}, have ${creditCheck.current_balance}`
                }
              </span>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
                <Settings className="h-6 w-6 text-purple-400" />
                <span>Cover Settings</span>
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Book Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter your book title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Author Name
                  </label>
                  <input
                    type="text"
                    name="author_name"
                    value={formData.author_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Author name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Genre
                  </label>
                  <select
                    name="genre"
                    value={formData.genre}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {genres.map(genre => (
                      <option key={genre} value={genre}>
                        {genre.charAt(0).toUpperCase() + genre.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    AI Provider
                  </label>
                  <div className="space-y-2">
                    {providers.map(provider => (
                      <label key={provider.id} className="flex items-center p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors">
                        <input
                          type="radio"
                          name="provider"
                          value={provider.id}
                          checked={formData.provider === provider.id}
                          onChange={handleInputChange}
                          className="text-purple-600 focus:ring-purple-500"
                        />
                        <div className="ml-3">
                          <div className="text-white font-medium">{provider.name}</div>
                          <div className="text-gray-400 text-sm">{provider.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Publishing Format
                  </label>
                  <div className="flex items-center space-x-3 p-3 bg-gray-800 rounded-lg">
                    <input
                      type="checkbox"
                      name="kdp_mode"
                      checked={formData.kdp_mode}
                      onChange={handleKdpModeToggle}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label className="text-white font-medium">
                      Generate for Amazon KDP Publishing
                    </label>
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    {formData.kdp_mode 
                      ? "Includes spine width calculation and KDP specifications"
                      : "Standard front cover for eBooks and general publishing"
                    }
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {formData.kdp_mode ? "KDP Format" : "Cover Size"}
                  </label>
                  <select
                    name="format_size"
                    value={formData.format_size}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {(formData.kdp_mode ? kdpFormats : ebookSizes).map(format => (
                      <option key={format.value} value={format.value}>
                        {format.label}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.kdp_mode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Page Count
                    </label>
                    <input
                      type="number"
                      name="page_count"
                      value={formData.page_count}
                      onChange={handleInputChange}
                      min="50"
                      max="800"
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Number of pages for spine calculation"
                    />
                    <p className="text-sm text-gray-400 mt-1">
                      Used to calculate spine width for KDP paperback covers
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Variations
                    </label>
                    <select
                      name="variations"
                      value={formData.variations}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="1">1 cover</option>
                      <option value="2">2 covers</option>
                      <option value="3">3 covers</option>
                      <option value="4">4 covers</option>
                    </select>
                  </div>

                  {!formData.kdp_mode && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Style
                      </label>
                      <select
                        name="cover_style"
                        value={formData.cover_style || 'modern'}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="modern">Modern</option>
                        <option value="classic">Classic</option>
                        <option value="minimalist">Minimalist</option>
                        <option value="vintage">Vintage</option>
                      </select>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Style Preferences (Optional)
                  </label>
                  <textarea
                    name="style_preferences"
                    value={formData.style_preferences}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., dark colors, minimalist, vintage style..."
                  />
                </div>

                <button
                  onClick={generateCovers}
                  disabled={loading || !formData.title.trim()}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5 mr-2" />
                      Generate Covers
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* KDP Specifications */}
            {kdpSpecs && (
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-green-500/20 mt-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <Info className="h-5 w-5 mr-2" />
                  KDP Specifications
                </h3>
                <div className="space-y-2 text-sm text-gray-300">
                  <div className="flex justify-between">
                    <span>Dimensions:</span>
                    <span>{kdpSpecs.width}" × {kdpSpecs.height}"</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Spine Width:</span>
                    <span>{kdpSpecs.spine_width.toFixed(3)}"</span>
                  </div>
                  <div className="flex justify-between">
                    <span>DPI:</span>
                    <span>{kdpSpecs.dpi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bleed:</span>
                    <span>{kdpSpecs.bleed}"</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Generated Covers */}
          <div className="lg:col-span-2">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
                  <Image className="h-6 w-6 text-purple-400" />
                  <span>Your Covers</span>
                </h2>
                {covers.length > 0 && (
                  <button
                    onClick={loadUserCovers}
                    className="text-purple-400 hover:text-purple-300 transition-colors flex items-center space-x-1"
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span>Refresh</span>
                  </button>
                )}
              </div>

              {loadingCovers ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="h-8 w-8 text-purple-400 animate-spin" />
                  <span className="ml-2 text-gray-400">Loading covers...</span>
                </div>
              ) : covers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {covers.map((cover, index) => (
                    <div key={`${cover.id}-${index}`} className="group relative">
                      <div className="aspect-[3/4] bg-gray-800 rounded-lg overflow-hidden border border-gray-600 hover:border-purple-500 transition-colors flex items-center justify-center">
                        <img
                          src={`data:image/png;base64,${cover.image_base64}`}
                          alt={`Cover for ${cover.title}`}
                          className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            console.error('Cover image failed to load');
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      
                      {/* Cover Info Overlay */}
                      <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex flex-col justify-between p-3">
                        <div>
                          <h3 className="text-white font-semibold text-sm mb-1 line-clamp-2">
                            {cover.title}
                          </h3>
                          <p className="text-gray-300 text-xs mb-1">
                            by {cover.author}
                          </p>
                          <p className="text-purple-400 text-xs capitalize mb-1">
                            {cover.genre}
                          </p>
                          {cover.dimensions && (
                            <p className="text-blue-400 text-xs">
                              {cover.book_format === 'paperback' ? `KDP ${cover.dimensions}` : `${cover.dimensions} px`}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1 text-xs text-gray-400">
                            <Clock className="h-3 w-3" />
                            <span>{new Date(cover.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex space-x-1">
                            <button
                              onClick={() => setSelectedCover(cover)}
                              className="bg-purple-600 text-white p-1 rounded hover:bg-purple-700 transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => downloadCover(cover)}
                              className="bg-green-600 text-white p-1 rounded hover:bg-green-700 transition-colors"
                              title="Download"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => confirmDelete(cover)}
                              className="bg-red-600 text-white p-1 rounded hover:bg-red-700 transition-colors"
                              title="Delete Cover"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Enhanced Badge */}
                      <div className="absolute top-2 left-2 z-10">
                        {getCoverBadge(cover)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Image className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">
                    No covers yet
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Generate your first cover using the settings on the left
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Cover Details Modal */}
      {selectedCover && (
        <CoverDetailsModal
          cover={selectedCover}
          onClose={() => setSelectedCover(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          cover={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(null)}
          onConfirm={deleteCover}
        />
      )}
    </div>
    </>
  );
};

export default CoverGeneratorPage;